import React from "react"
import { Field } from "redux-form/immutable"
import PropTypes from "prop-types"
import classNames from "classnames"

import ReduxFormFieldWrapper from "app/static/frontend/forms/components/ReduxFormFieldWrapper"

import "./CheckboxField.styl"

/**
 * CheckboxField component for simple checkbox inputs with label support
 *
 * Features:
 * - Redux Form integration
 * - HTML content support in checkboxLabel (for compliance text, links, etc.)
 * - Consistent styling with toggle-field-container when used in that context
 * - Campaign page styling support
 * - Accessibility attributes
 * - Error and warning states
 *
 * Usage:
 * <CheckboxField
 *   name="agreement"
 *   label="Agreement"
 *   checkboxLabel="I agree to <strong>Terms</strong> and <a href='/privacy'>Privacy Policy</a>"
 *   isCampaignPage={true}
 * />
 */
const renderCheckboxInput = (props) => {
    const {
        input: { value, onChange, onBlur, onFocus },
        meta: { error, warning, touched },
        accessibilityId,
        dataCy,
        displayErrorWithoutTouch,
        label,
        labelStyle,
        style,
        tooltipText,
        showAsterisk,
        disabled,
        className,
        isCampaignPage,
        checkboxLabel,
    } = props

    const handleChange = (e) => {
        onChange(e.target.checked)
    }

    return (
        <ReduxFormFieldWrapper
            className="checkbox"
            dataCy={dataCy}
            displayErrorWithoutTouch={displayErrorWithoutTouch}
            error={error}
            label={label}
            labelStyle={labelStyle}
            style={style}
            tooltipText={tooltipText}
            touched={touched}
            warning={warning}
            accessibilityId={accessibilityId}
            showAsterisk={showAsterisk}
        >
            <div
                className={classNames("checkbox-field-container", className, {
                    "campaign-page": isCampaignPage,
                })}
            >
                <label className="control-label">
                    <input
                        type="checkbox"
                        checked={Boolean(value)}
                        onChange={handleChange}
                        onBlur={onBlur}
                        onFocus={onFocus}
                        disabled={disabled}
                        data-cy={dataCy}
                        id={accessibilityId}
                        className={classNames("checkbox-field-input", {
                            "checkbox-field-input-error": error && (displayErrorWithoutTouch || touched),
                            "checkbox-field-input-warning": warning && (displayErrorWithoutTouch || touched),
                            "checkbox-field-input-disabled": disabled,
                        })}
                        aria-required={props["aria-required"]}
                    />
                    <span className="checkbox-field-checkmark" />
                    {checkboxLabel && (
                        <span className="checkbox-field-text" dangerouslySetInnerHTML={{ __html: checkboxLabel }} />
                    )}
                </label>
            </div>
        </ReduxFormFieldWrapper>
    )
}

export const CheckboxField = (props) => {
    return <Field {...props} component={renderCheckboxInput} />
}

CheckboxField.propTypes = {
    // Redux Form props
    name: PropTypes.string.isRequired,

    // CheckboxField props
    accessibilityId: PropTypes.string,
    checkboxLabel: PropTypes.string,
    className: PropTypes.string,
    dataCy: PropTypes.string,
    disabled: PropTypes.bool,
    displayErrorWithoutTouch: PropTypes.bool,
    isCampaignPage: PropTypes.bool,
    label: PropTypes.string,
    labelStyle: PropTypes.object,
    showAsterisk: PropTypes.bool,
    style: PropTypes.object,
    tooltipText: PropTypes.string,
    validate: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.func)]),
    warn: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.func)]),
}

CheckboxField.defaultProps = {
    displayErrorWithoutTouch: false,
    disabled: false,
    isCampaignPage: false,
}

export default CheckboxField
